// Temporary component until we migrate to ReUI
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { twMerge } from 'tailwind-merge';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import { ErrorMessage } from '@/components/Message/ErrorMessage';

type OptionType = {
  label: string;
  id: string;
};

type SelectAutoCompleteProps<TSchema extends FieldValues> = {
  label?: string;
  name: Path<TSchema>;
  disabled?: boolean;
  control: Control<TSchema>;
  size?: 'small' | 'medium';
  options: OptionType[];
  placeholder?: string;
  value?: OptionType;
  rules?: RegisterOptions<TSchema, Path<TSchema>>;
  containerClassName?: string;
  labelClassName?: string;
  autocompleteClassName?: string;
  errorMessage?: string;
};

export const SelectAutoComplete = <TSchema extends FieldValues>({
  label,
  control,
  name,
  options,
  placeholder,
  size = 'small',
  disabled = false,
  rules,
  containerClassName,
  labelClassName,
  autocompleteClassName,
  errorMessage,
}: SelectAutoCompleteProps<TSchema>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const safeOptions = Array.isArray(options) ? options : [];
        const valueObj =
          safeOptions.find((o) => o.id === (field.value as string | null)) ??
          null;
        return (
          <div className="w-full flex flex-1 flex-col gap-0.75">
            <div className={twMerge('flex flex-1 gap-2', containerClassName)}>
              {label && (
                <p
                  className={twMerge(
                    'text-sm text-gray-700 self-center',
                    labelClassName
                  )}
                >
                  {label}
                </p>
              )}
              <Autocomplete
                className={twMerge('flex flex-1', autocompleteClassName)}
                sx={{
                  '& .MuiInputBase-input': {
                    color: '#5B5B5B',
                  },
                }}
                slotProps={{
                  listbox: {
                    className: 'text-sm text-gray-700',
                  },
                }}
                size={size}
                disablePortal
                disabled={disabled}
                selectOnFocus
                clearOnBlur={false}
                onBlur={field.onBlur}
                value={valueObj}
                getOptionLabel={(o) => o.label}
                // isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, v) => field.onChange(v?.id ?? null)}
                options={safeOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={placeholder}
                    sx={{
                      '& .MuiInputBase-input::placeholder': {
                        color: 'black',
                        opacity: 0.7,
                      },
                    }}
                  />
                )}
              />
            </div>
            {errorMessage && <ErrorMessage message={errorMessage} />}
          </div>
        );
      }}
    />
  );
};
